import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Eye, EyeOff, GripVertical, Loader2, MessageSquare, Plus, Workflow } from "lucide-react";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { CAPTURE_WIRED_STAGES, STEP_DEFS, stageTemplateLock } from "../../lib/journey";
import {
  useTenantJourneyStages,
  useSaveTenantJourneyStages,
  useTenantTemplates,
  useSaveTenantTemplates,
  useWaTemplates,
  useDealerTemplateOptions,
  useFeatureFlags,
  useDealerTemplateAssignments,
  useAssignDealerTemplate,
  useRemoveDealerAssignment,
  useTenantRoles,
} from "../../lib/api";
import { pickComplete, replyButtons, stageTemplates } from "./StageTemplatePicker";
import {
  BuiltInMessage,
  StageForm,
  DEFAULT_BLOCKS,
  TRIGGERS,
  Row,
  Segmented,
  StageHeader,
} from "./StageDetail";
import { BUILT_IN_RULES, rowPhases } from "./builtInRules";
import JourneyPreview from "./JourneyPreview";
import AddExistingTemplate, { AddTemplateContext } from "./templates/AddExistingTemplate";
import { GhostButton, PrimaryButton, Select, Toggle, cardClass, inputClass } from "./primitives";

// group_delete_delay_minutes is stored in minutes (same "one unit, converted
// at the edges" rule as post_delivery_feedback's own delay_minutes); the
// editor lets the brand pick whichever unit reads naturally — minutes for a
// quick test, days for the real production wait — and converts at the edge.
const GROUP_DELETE_DEFAULT_MINUTES = 10080; // 7 days
const GROUP_DELETE_MAX_MINUTES = 129600; // 90 days — matches the server-side cap
const GROUP_DELETE_UNITS = [
  { key: "minutes", label: "minutes", perMinute: 1 },
  { key: "hours", label: "hours", perMinute: 60 },
  { key: "days", label: "days", perMinute: 1440 },
];
const unitFor = (minutes) => {
  const m = Number(minutes) || 0;
  if (m > 0 && m % 1440 === 0) return "days";
  if (m > 0 && m % 60 === 0) return "hours";
  return "minutes";
};
const valueInUnit = (minutes, unitKey) => {
  const perMinute = GROUP_DELETE_UNITS.find((u) => u.key === unitKey).perMinute;
  return Math.round((Number(minutes) || 0) / perMinute);
};
const toMinutes = (value, unitKey) => {
  const perMinute = GROUP_DELETE_UNITS.find((u) => u.key === unitKey).perMinute;
  return Math.min(GROUP_DELETE_MAX_MINUTES, Math.max(0, Number(value) || 0) * perMinute);
};

const NO_STAGE = "__no_stage__";

// The stages whose message carries one file type, chosen here: stage -> the message's template key.
const FILE_CHOICE_STAGES = { jobcard: "job_card", pre_invoice: "pre_invoice", estimate: "estimate", invoice: "invoice" };
// The file such a message carries: its choice, and the template type that carries it.
const FILE_CHOICES = [
  ["document", "Document"],
  ["image", "Photo"],
  ["video", "Video"],
];

// "Feedback" (feedback / feedback_cia / feedback_nps) is redundant with Pre
// Delivery Feedback and Post Delivery Feedback here — hidden from this list
// only. The stage itself and its templates are untouched.
const HIDDEN_FROM_LIST = new Set(["feedback"]);

// Stages in the Hyundai Pre Delivery auto-chain: seconds after the previous
// event before this stage's message is sent.
const CHAIN_DELAY_DEFAULTS = {
  pre_delivery_feedback: 4,
  next_service_due: 0,
  post_delivery_feedback: 5,
};

const newCustomKey = () => `custom_${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

const keywordsToText = (list) => (list || []).join("\n");
const textToKeywords = (text) =>
  (text || "")
    .split("\n")
    .map((k) => k.trim())
    .filter(Boolean);

// Merges the stored overlay onto STEP_DEFS so the editor always shows every
// stage, including ones this tenant has never touched.
export function toEditorRows(saved, keywordDefaults) {
  const byKey = new Map((saved || []).map((s) => [s.stage_key, s]));
  const rows = STEP_DEFS.filter((d) => !HIDDEN_FROM_LIST.has(d.key)).map((d, i) => {
    const s = byKey.get(d.key);
    let delay = null;
    if (d.key in CHAIN_DELAY_DEFAULTS) {
      delay =
        typeof s?.auto_send_delay_seconds === "number"
          ? s.auto_send_delay_seconds
          : CHAIN_DELAY_DEFAULTS[d.key];
    }
    return {
      stage_key: d.key,
      defaultLabel: d.label,
      label: s?.label || d.label,
      is_visible: s ? s.is_visible !== false : true,
      sort_order: typeof s?.sort_order === "number" ? s.sort_order : i,
      auto_send_delay_seconds: delay,
      capture_from_whatsapp: s?.capture_from_whatsapp ?? false,
      keywordsText: keywordsToText(s?.capture_keywords?.length ? s.capture_keywords : keywordDefaults[d.key]),
      // A built-in stage keeps what it saved: all its settings on the engine, or just its keyword rule
      // and (Invitations Sent) who gets an invitation. Dropping invite_roles here showed every role
      // as picked and the next Save erased the brand's choice.
      blocks: s?.blocks?.trigger
        ? s.blocks
        : {
            keyword_sent_by: s?.blocks?.keyword_sent_by || "staff",
            keyword_match: s?.blocks?.keyword_match || "contains",
            ...(Array.isArray(s?.blocks?.invite_roles) ? { invite_roles: s.blocks.invite_roles } : {}),
          },
    };
  });
  // The stages the brand added itself.
  (saved || [])
    .filter((s) => s.is_custom)
    .forEach((s) =>
      rows.push({
        stage_key: s.stage_key,
        defaultLabel: s.label,
        label: s.label,
        is_visible: s.is_visible !== false,
        sort_order: typeof s.sort_order === "number" ? s.sort_order : rows.length,
        auto_send_delay_seconds: null,
        capture_from_whatsapp: !!s.capture_from_whatsapp,
        keywordsText: keywordsToText(s.capture_keywords),
        is_custom: true,
        blocks: { ...DEFAULT_BLOCKS, ...(s.blocks || {}) },
      }),
    );
  rows.sort((a, b) => a.sort_order - b.sort_order);
  // Older saves started a stage after another one, a button or a keyword, or jumped to a stage
  // from a rule. The list order does each of those now, as the API reads them too.
  rows.forEach((r) => {
    const b = r.blocks || {};
    if (!b.trigger) return;
    const trigger = TRIGGERS.some(([k]) => k === b.trigger) ? b.trigger : b.trigger === "advisor" ? "manual" : "automatic";
    r.blocks = { ...b, trigger, capture_rules: (b.capture_rules || []).filter((rule) => rule.action !== "start") };
  });
  return rows;
}

const toTemplateRows = (data) => (data?.rows || []).map((r) => ({ ...r }));

function StageListRow({ row, index, total, selected, onSelect, onMove, onToggleVisible, first, last, drag }) {
  const dropLine = drag.over === index && drag.from !== null && drag.from !== index ? (drag.from < index ? "after" : "before") : "";
  return (
    <div
      className={`group/row relative flex select-none items-center ${drag.from === index ? "opacity-40" : ""}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
        drag.start(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        drag.hover(index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        drag.drop(index);
      }}
      onDragEnd={drag.end}
    >
      {dropLine && <span aria-hidden="true" className={`absolute left-3 right-3 z-[2] h-[2px] rounded-full bg-[#2667B5] ${dropLine === "before" ? "-top-px" : "-bottom-px"}`} />}
      {/* The journey rail: one line through every stage's node. */}
      <span aria-hidden="true" className={`absolute left-[27px] w-px bg-[#E4E7EC] ${first ? "top-1/2" : "top-0"} ${last ? "bottom-1/2" : "bottom-0"}`} />
      <button
        type="button"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
            e.preventDefault();
            onMove(e.key === "ArrowUp" ? -1 : 1);
          }
        }}
        aria-current={selected ? "true" : undefined}
        aria-description={`Stage ${index + 1} of ${total}. Drag, or Alt and arrow keys, to move.`}
        className={`relative flex h-10 min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg border-0 pl-[17px] pr-9 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] ${
          selected ? "bg-[#EFF5FC]" : "bg-transparent hover:bg-[#F9FAFB]"
        }`}
      >
        <span
          className={`relative z-[1] flex h-[21px] w-[21px] flex-none items-center justify-center text-[10.5px] font-semibold tabular-nums ${row.is_custom ? "rounded-[6px]" : "rounded-full"} ${
            selected ? "bg-[#2667B5] text-white" : row.is_visible ? "bg-white text-[#475467] ring-1 ring-[#D0D5DD]" : "bg-white text-[#98A2B3] ring-1 ring-dashed ring-[#D0D5DD]"
          }`}
        >
          {index + 1}
        </span>
        <span className={`truncate text-[13.5px] ${selected ? "font-semibold text-[#1F5596]" : row.is_visible ? "font-medium text-[#344054]" : "font-medium text-[#98A2B3] line-through decoration-[#D0D5DD]"}`}>{row.label}</span>
        {row.is_custom && <span className="flex-none rounded-full bg-[#EFF5FC] px-[7px] py-[1px] text-[10.5px] font-semibold text-[#2667B5]">Custom</span>}
      </button>
      <GripVertical size={14} aria-hidden="true" className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 cursor-grab text-[#C1C7D0] opacity-0 transition-opacity duration-150 group-hover/row:opacity-100" />
      <button
        type="button"
        onClick={onToggleVisible}
        aria-label={row.is_visible ? `Hide ${row.label}` : `Show ${row.label}`}
        className={`absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-[#98A2B3] transition-opacity duration-150 hover:bg-[#F2F4F7] hover:text-[#344054] ${
          row.is_visible ? "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100" : "opacity-100"
        }`}
      >
        {row.is_visible ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
      </button>
    </div>
  );
}

// The messages a built-in stage (or "Other messages") sends, one block each.
function TemplateList({ templates, filterKey, lock, errors, onPatch, usable, fields, picks, onPick, stepKeys }) {
  const match = filterKey === NO_STAGE ? (r) => !r.stage : (r) => r.stage === filterKey;
  const rows = templates.filter(match);
  if (!rows.length) return null;
  return (
    <div className="flex flex-col gap-7">
      {rows.map((r) => (
        <BuiltInMessage
          key={r.template_key}
          tpl={r}
          showName={rows.length > 1}
          templates={stepKeys.has(r.template_key) ? usable : []}
          fields={fields}
          pick={picks[r.template_key] || null}
          onPick={(p) => onPick(r.template_key, p)}
          onPatch={(p) => onPatch(r.template_key, p)}
          lock={lock}
          error={errors[r.template_key]}
        />
      ))}
    </div>
  );
}

// Who the Invitations Sent stage invites, picked from a dropdown. The customer always gets one.
// No saved choice (value undefined) means every role, which is what the app did before this setting.
function InviteRoles({ roles, value, onChange }) {
  const chosen = value === undefined || value === null ? roles.map((r) => r.code) : value;
  const flip = (code, on) => onChange(on ? roles.map((r) => r.code).filter((c) => c === code || chosen.includes(c)) : chosen.filter((c) => c !== code));
  const names = roles.filter((r) => chosen.includes(r.code)).map((r) => r.label);
  const summary = ["Customer", ...names.slice(0, 2)].join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Who gets an invitation" className={`${inputClass} flex w-full max-w-[420px] items-center justify-between gap-3 text-left`}>
          <span className="truncate">{summary}</span>
          <ChevronDown size={16} className="shrink-0 text-[#667085]" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[260px]">
        <DropdownMenuCheckboxItem checked disabled>
          Customer (always)
        </DropdownMenuCheckboxItem>
        {roles.map((r) => (
          <DropdownMenuCheckboxItem key={r.code} checked={chosen.includes(r.code)} onCheckedChange={(on) => flip(r.code, !!on)} onSelect={(e) => e.preventDefault()}>
            {r.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function StageEditor({ tenantId }) {
  const { data, isLoading } = useTenantJourneyStages(tenantId);
  const { data: rolesData } = useTenantRoles(tenantId);
  // The service advisor comes from the booking itself (code "sa"), so it is offered even when the brand has no such role.
  const inviteRoleOptions = useMemo(() => {
    const list = (rolesData?.rows || []).filter((r) => r.code).map((r) => ({ code: r.code, label: r.label || r.code }));
    return list.some((r) => r.code === "sa") ? list : [{ code: "sa", label: "Service advisor" }, ...list];
  }, [rolesData]);
  const { data: templatesData, isLoading: templatesLoading } = useTenantTemplates(tenantId);
  const saveStages = useSaveTenantJourneyStages(tenantId);
  const saveTemplates = useSaveTenantTemplates(tenantId);
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [errors, setErrors] = useState({ label: {}, templates: {} });
  const { data: waTemplates } = useWaTemplates(tenantId);
  const { data: stepOptions } = useDealerTemplateOptions(tenantId);
  const { data: brandFlags } = useFeatureFlags(tenantId);
  const engine = brandFlags?.journey_engine === true;
  const { data: assignments } = useDealerTemplateAssignments(tenantId);
  const assign = useAssignDealerTemplate(tenantId);
  const unassign = useRemoveDealerAssignment(tenantId);
  const usable = useMemo(() => stageTemplates(waTemplates), [waTemplates]);
  const [addingTemplate, setAddingTemplate] = useState(null); // {onAdded} while the add-from-Chat360 dialog is open
  const fields = stepOptions?.fields || [];
  // The whole brand's template choice for each step: {template_key: {id, field_map, assignment}}.
  const baselinePicks = useMemo(() => {
    const out = {};
    (assignments || [])
      .filter((a) => a.dealer_id == null)
      .forEach((a) => {
        out[a.template_key] = { id: a.wa_template_request_id, field_map: a.field_map || {}, assignment: a.id };
      });
    return out;
  }, [assignments]);
  const [picks, setPicks] = useState({});
  useEffect(() => setPicks(baselinePicks), [baselinePicks]);

  const keywordDefaults = useMemo(() => data?.keyword_defaults || {}, [data]);
  const baselineRows = useMemo(
    () => (data ? toEditorRows(data.rows, keywordDefaults) : []),
    [data, keywordDefaults],
  );
  const baselineTemplates = useMemo(() => toTemplateRows(templatesData), [templatesData]);

  useEffect(() => setRows(baselineRows), [baselineRows]);
  useEffect(() => setTemplates(baselineTemplates), [baselineTemplates]);

  useEffect(() => {
    if (!selectedKey && rows.length) setSelectedKey(rows[0].stage_key);
  }, [rows, selectedKey]);

  const hasNoStageTemplates = templates.some((r) => !r.stage);

  const baselineTemplatesByKey = useMemo(
    () => new Map(baselineTemplates.map((t) => [t.template_key, JSON.stringify(t)])),
    [baselineTemplates],
  );
  const changedTemplates = templates.filter(
    (t) => baselineTemplatesByKey.get(t.template_key) !== JSON.stringify(t),
  );
  const pickKey = (p) => (p ? JSON.stringify([p.id, p.field_map || {}]) : "");
  const changedPicks = Object.keys({ ...picks, ...baselinePicks }).filter((k) => pickKey(picks[k]) !== pickKey(baselinePicks[k]));
  const dirty = JSON.stringify(rows) !== JSON.stringify(baselineRows) || changedTemplates.length > 0 || changedPicks.length > 0;

  const captureOffered = (key) => CAPTURE_WIRED_STAGES.has(key) || rows.some((r) => r.stage_key === key && r.is_custom);
  const addStage = () => {
    const key = newCustomKey();
    setRows((rs) => [
      ...rs,
      { stage_key: key, defaultLabel: "New stage", label: "New stage", is_visible: true, sort_order: rs.length, auto_send_delay_seconds: null,
        capture_from_whatsapp: false, keywordsText: "", is_custom: true, blocks: { ...DEFAULT_BLOCKS } },
    ]);
    setSelectedKey(key);
  };
  const removeStage = (key) => {
    setRows((rs) => rs.filter((r) => r.stage_key !== key));
    setPicks((p) => ({ ...p, [key]: null }));
    setSelectedKey(null);
  };

  const selectedIndex = rows.findIndex((r) => r.stage_key === selectedKey);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const onNoStage = selectedKey === NO_STAGE;
  const pdfTemplateRow = templates.find((t) => t.template_key === "post_delivery_feedback");
  const escTemplateRow = templates.find((t) => t.template_key === "workshop_contacts");

  // Drag a stage to a new place: it lands where the blue line shows.
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const moveTo = (from, to) => {
    if (from === null || to === null || from === to) return;
    setRows((rs) => {
      const next = [...rs];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };
  const drag = {
    from: dragFrom,
    over: dragOver,
    start: (i) => setDragFrom(i),
    hover: (i) => dragOver !== i && setDragOver(i),
    drop: (i) => {
      moveTo(dragFrom, i);
      setDragFrom(null);
      setDragOver(null);
    },
    end: () => {
      setDragFrom(null);
      setDragOver(null);
    },
  };

  // Opening a stage brings its settings into view, so a stage far down the list does not open
  // with its top scrolled away.
  const detailRef = useRef(null);
  useEffect(() => {
    const el = detailRef.current;
    if (el && el.getBoundingClientRect().top < 72) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedKey]);

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    setRows(next);
  };

  const setField = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.stage_key === key ? { ...r, ...patch } : r)));

  const patchTemplate = (key, patch) =>
    setTemplates((ts) => ts.map((t) => (t.template_key === key ? { ...t, ...patch } : t)));

  const validate = () => {
    const label = {};
    rows.forEach((r) => {
      if (!r.label.trim()) label[r.stage_key] = "Enter a stage name.";
    });
    const tpl = {};
    changedTemplates.forEach((t) => {
      if (t.send_mode === "direct" && !(t.body || "").trim()) tpl[t.template_key] = "Enter the message text.";
    });
    Object.entries(FILE_CHOICE_STAGES).forEach(([stage, key]) => {
      const file = templates.find((t) => t.template_key === key)?.config?.attachment;
      const picked = usable.find((t) => t.id === picks[key]?.id);
      if (file && picked && picked.template_type !== file) label[stage] = `Choose a ${file === "image" ? "photo" : file} template for ${rows.find((r) => r.stage_key === stage)?.label || stage}.`;
    });
    Object.entries(picks).forEach(([k, p]) => {
      if (!pickComplete(p, usable)) {
        const owner = rows.find((r) => r.stage_key === k)?.stage_key || (stepOptions?.steps || []).find((st) => st.key === k)?.stage;
        if (owner) label[owner] = label[owner] || "Set the template's fields on the Templates tab.";
      }
    });
    rows
      .filter((r) => r.is_custom)
      .forEach((r) => {
        const b = r.blocks || {};
        (b.capture_rules || []).forEach((rule, n) => {
          const problem = rule.source === "button" && !rule.button
            ? "needs a button"
            : rule.source !== "button" && !(rule.keywords || []).length
            ? "needs a keyword"
            : rule.action === "reply" && !rule.reply_template_id
              ? "needs a reply message"
            : rule.action === "status" && !(rule.status || "").trim()
              ? "needs a status"
              : rule.action === "escalate" && !rule.escalation
                ? "needs an escalation"
                : rule.action === "rating" && (rule.score == null || rule.score === "")
                  ? "needs a score"
                  : "";
          if (problem) label[r.stage_key] = label[r.stage_key] || `Capture rule ${n + 1} ${problem}.`;
        });
      });
    setErrors({ label, templates: tpl });
    const firstStage =
      Object.keys(label)[0] ||
      templates.find((t) => tpl[t.template_key])?.stage ||
      (Object.keys(tpl).length ? NO_STAGE : null);
    if (firstStage) setSelectedKey(firstStage);
    return !firstStage;
  };

  const onSave = async () => {
    if (!validate()) return;
    try {
      await saveStages.mutateAsync(
        rows.map((r) => {
          const keywords = textToKeywords(r.keywordsText);
          const matchesDefault =
            keywordsToText(keywords) === keywordsToText(keywordDefaults[r.stage_key]);
          return {
            stage_key: r.stage_key,
            label: r.label.trim(),
            is_visible: r.is_visible,
            auto_send_delay_seconds: r.stage_key in CHAIN_DELAY_DEFAULTS ? r.auto_send_delay_seconds : null,
            capture_from_whatsapp: r.is_custom ? (r.blocks?.capture_rules || []).some((rule) => rule.source !== "button") : captureOffered(r.stage_key) ? !!r.capture_from_whatsapp : false,
            capture_keywords: r.is_custom
              ? (r.blocks?.capture_rules || []).filter((rule) => rule.source !== "button").flatMap((rule) => rule.keywords || [])
              : matchesDefault ? null : keywords,
            is_custom: !!r.is_custom,
            blocks: r.blocks || null,
          };
        }),
      );
      if (changedTemplates.length) {
        await saveTemplates.mutateAsync(
          changedTemplates.map((t) => ({
            template_key: t.template_key,
            send_mode: t.send_mode,
            template_name: (t.template_name || "").trim(),
            body: t.body || "",
            enabled: t.enabled,
            config: t.config || null,
          })),
        );
      }
      // Template choices go after the stages, so a new stage exists when its template is given to it.
      const live = new Set(rows.map((r) => r.stage_key));
      for (const k of changedPicks) {
        const p = picks[k];
        const base = baselinePicks[k];
        // An empty field map follows the template's own fields (Templates tab).
        if (p) await assign.mutateAsync({ id: p.id, template_key: k, field_map: {} });
        else if (base?.assignment && (live.has(k) || !k.startsWith("custom_"))) await unassign.mutateAsync(base.assignment);
      }
      toast.success("Service journey saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const onDiscard = () => {
    setRows(baselineRows);
    setTemplates(baselineTemplates);
    setPicks(baselinePicks);
    setErrors({ label: {}, templates: {} });
  };

  if (isLoading || templatesLoading) {
    return (
      <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  const saving = saveStages.isPending || saveTemplates.isPending || assign.isPending || unassign.isPending;
  const visibleCount = rows.filter((r) => r.is_visible).length;
  const lock = selectedRow ? stageTemplateLock(selectedRow.stage_key, selectedRow) : { locked: false, reason: "" };
  const stepKeys = new Set((stepOptions?.steps || []).map((st) => st.key));
  const setPick = (key, p) => setPicks((all) => ({ ...all, [key]: p }));

  // Phase headings: shown on the first row of each phase.
  const phases = rowPhases(rows).map((p, i, all) => (i === 0 || p !== all[i - 1] ? p : ""));

  const saveBar = dirty && (
    <div className="sticky bottom-0 z-10 -mx-8 -mb-8 mt-8 flex items-center justify-between gap-4 rounded-b-xl border-t border-[#EAECF0] bg-white/95 px-8 py-4 backdrop-blur-sm">
      <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-[#344054]">
        <span className="h-2 w-2 rounded-full bg-[#F79009]" aria-hidden="true" />
        Unsaved changes
      </span>
      <div className="flex items-center gap-2">
        <GhostButton onClick={onDiscard}>Discard</GhostButton>
        <PrimaryButton onClick={onSave} disabled={saving}>
          {saving && <Loader2 className="animate-spin" size={14} />}
          Save changes
        </PrimaryButton>
      </div>
    </div>
  );

  // A built-in stage is the same form as one the brand adds, filled in with what it does today.
  // Its settings map back onto the columns its code already reads.
  const builtIn = () => {
    const r = selectedRow;
    if (engine && !["created", "invites"].includes(r.stage_key)) {
      // Every stage runs on the engine: a built-in stage is edited exactly like one the brand added.
      // Its saved settings: the starter journey saved them when the switch was turned on.
      const blocks = r.blocks?.trigger ? r.blocks : { trigger: "manual", ...(r.blocks || {}) };
      return (
        <StageForm
          key={r.stage_key}
          row={{ ...r, blocks }}
          templates={usable}
          fields={fields}
          pick={picks[r.stage_key] || null}
          onPick={(p) => setPick(r.stage_key, p)}
          onChange={(patch) => setField(r.stage_key, patch)}
        />
      );
    }
    const rules = BUILT_IN_RULES[r.stage_key] || {};
    const hasMessage = templates.some((t) => t.stage === r.stage_key);
    const delayStage = r.stage_key in CHAIN_DELAY_DEFAULTS;
    const delay = r.auto_send_delay_seconds ?? CHAIN_DELAY_DEFAULTS[r.stage_key];
    const capture = captureOffered(r.stage_key);
    const keywordOn = capture && r.capture_from_whatsapp;
    const escalation = r.stage_key === "initial" && escTemplateRow;
    const channel = pdfTemplateRow?.config?.channel === "one_on_one" ? "one_on_one" : "group";
    // Estimate, Job Card, Pre Invoice and Invoice send one file type, chosen here; the template list and the upload follow it.
    const fileKey = FILE_CHOICE_STAGES[r.stage_key];
    const fileRow = fileKey ? templates.find((t) => t.template_key === fileKey) : null;
    const chosenFile = fileRow?.config?.attachment || "";
    const setChosenFile = (attachment) => {
      const { attachment: _old, ...rest } = fileRow.config || {};
      patchTemplate(fileKey, { config: attachment ? { ...rest, attachment } : Object.keys(rest).length ? rest : null });
      const picked = usable.find((t) => t.id === picks[fileKey]?.id);
      if (attachment && picked && picked.template_type !== attachment) setPick(fileKey, null);
    };
    const messageTemplates = chosenFile ? usable.filter((t) => t.template_type === chosenFile) : usable;
    const setPdf = (patch) => patchTemplate("post_delivery_feedback", { config: { ...(pdfTemplateRow.config || {}), ...patch } });

    const formRow = {
      ...r,
      blocks: {
        trigger: escalation ? (escTemplateRow.config?.send_timing === "after_delivery" ? "automatic" : "customer_joined") : "automatic",
        capture_rules: keywordOn
          ? [{ keywords: textToKeywords(r.keywordsText), sent_by: r.blocks?.keyword_sent_by || "staff", match: r.blocks?.keyword_match || "contains", action: "done" }]
          : [],
      },
    };
    const onFormChange = (patch) => {
      const b = patch.blocks;
      if (!b) return;
      if (escalation && b.trigger !== formRow.blocks.trigger) {
        patchTemplate("workshop_contacts", { config: { ...(escTemplateRow.config || {}), send_timing: b.trigger === "automatic" ? "after_delivery" : "initial" } });
      }
      const rule = (b.capture_rules || [])[0];
      if (rule) {
        setField(r.stage_key, {
          capture_from_whatsapp: true,
          keywordsText: (rule.keywords || []).join("\n"),
          blocks: { ...(r.blocks || {}), keyword_sent_by: rule.sent_by, keyword_match: rule.match },
        });
      } else if (keywordOn) {
        setField(r.stage_key, { capture_from_whatsapp: false });
      }
    };

    return (
      <StageForm
        key={r.stage_key}
        row={formRow}
        templates={usable}
        fields={fields}
        pick={null}
        onPick={() => {}}
        onChange={onFormChange}
        fixed={{
          trigger: escalation ? null : rules.trigger || "Manual",
          triggerOptions: [["customer_joined", "Customer joined"], ["automatic", "Automatic"]],
          delay: delayStage
            ? { seconds: delay, max: 86400, units: [["sec", 1], ["min", 60], ["hours", 3600]], onChange: (v) => setField(r.stage_key, { auto_send_delay_seconds: v }) }
            : null,
          message: hasMessage ? (
            <TemplateList
              templates={templates}
              filterKey={r.stage_key}
              lock={lock}
              errors={errors.templates}
              onPatch={patchTemplate}
              usable={messageTemplates}
              fields={fields}
              picks={picks}
              onPick={setPick}
              stepKeys={stepKeys}
            />
          ) : null,
          recipient:
            r.stage_key === "invites" ? (
              <InviteRoles
                roles={inviteRoleOptions}
                value={r.blocks?.invite_roles}
                onChange={(v) => setField("invites", { blocks: { ...(r.blocks || {}), invite_roles: v } })}
              />
            ) : r.stage_key === "post_delivery_feedback" && pdfTemplateRow ? (
              <Segmented label="Send to" options={[["group", "Group"], ["one_on_one", "Customer only"]]} value={channel} onChange={(v) => setPdf({ channel: v })} />
            ) : (
              rules.recipient || "Group"
            ),
          attachment:
            fileRow ? (
              <Select aria-label="Attachment" value={chosenFile} onChange={(e) => setChosenFile(e.target.value)} className="w-full max-w-[320px]">
                <option value="">Not chosen</option>
                {FILE_CHOICES.map(([k, word]) => (
                  <option key={k} value={k}>
                    {word}
                  </option>
                ))}
              </Select>
            ) : (
              rules.attachment
            ),
          choose: !!fileRow,
          replies: rules.replies,
          oneRule: true,
          noCapture: !capture,
          more:
            r.stage_key === "post_delivery_feedback" && pdfTemplateRow ? (
              <>
                <Row label="Delete group">
                  <div className="flex items-center gap-3">
                    <Toggle checked={!!pdfTemplateRow.config?.group_delete_enabled} onChange={(on) => setPdf({ group_delete_enabled: on })} label="Delete the WhatsApp group" />
                    <span className="text-[13px] text-[#667085]">{pdfTemplateRow.config?.group_delete_enabled ? "On" : "Off"}</span>
                  </div>
                </Row>
                {pdfTemplateRow.config?.group_delete_enabled &&
                  (() => {
                    const rawMinutes = pdfTemplateRow.config?.group_delete_delay_minutes ?? GROUP_DELETE_DEFAULT_MINUTES;
                    const unit = unitFor(rawMinutes);
                    return (
                      <Row label="Delete after">
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={0}
                            className={`${inputClass} !w-[96px] tabular-nums`}
                            value={valueInUnit(rawMinutes, unit)}
                            onChange={(e) => setPdf({ group_delete_delay_minutes: toMinutes(e.target.value, unit) })}
                            aria-label="Delete the group after"
                          />
                          <Select
                            aria-label="Unit"
                            value={unit}
                            onChange={(e) => setPdf({ group_delete_delay_minutes: toMinutes(valueInUnit(rawMinutes, unit), e.target.value) })}
                            className="!w-[112px]"
                          >
                            {GROUP_DELETE_UNITS.map((u) => (
                              <option key={u.key} value={u.key}>
                                {u.label}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </Row>
                    );
                  })()}
              </>
            ) : null,
        }}
      />
    );
  };


  return (
    <AddTemplateContext.Provider value={(onAdded) => setAddingTemplate({ onAdded })}>
      <div className="mt-7 grid grid-cols-1 items-start gap-6 lg:grid-cols-[288px_minmax(0,1fr)]">
        <nav aria-label="Stages" className={`overflow-hidden lg:sticky lg:top-[88px] ${cardClass}`}>
          <div className="flex items-baseline justify-between px-4 pb-2 pt-4">
            <span className="text-[14px] font-semibold text-[#101828]">Stages</span>
            <span className="text-[12.5px] tabular-nums text-[#667085]">
              {visibleCount} of {rows.length} visible
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1 border-b border-[#EAECF0] px-2 pb-2">
            <button
              type="button"
              onClick={addStage}
              className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg text-[13.5px] font-medium text-[#2667B5] transition-colors duration-150 hover:bg-[#EFF5FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6]"
            >
              <Plus size={15} aria-hidden="true" /> Add stage
            </button>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-lg text-[13.5px] font-medium text-[#2667B5] transition-colors duration-150 hover:bg-[#EFF5FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6]"
            >
              <Workflow size={15} aria-hidden="true" /> Preview
            </button>
          </div>
          <div className="max-h-[calc(100vh-220px)] overflow-y-auto px-2 pb-2 pt-2">
            {rows.map((r, i) => (
              <div key={r.stage_key}>
                {phases[i] && <div className={`px-[14px] pb-1 text-[12px] font-medium text-[#98A2B3] ${i === 0 ? "pt-1" : "pt-4"}`}>{phases[i]}</div>}
                <StageListRow
                  row={r}
                  index={i}
                  total={rows.length}
                  first={!!phases[i]}
                  last={i === rows.length - 1 || !!phases[i + 1]}
                  selected={selectedKey === r.stage_key}
                  onSelect={() => setSelectedKey(r.stage_key)}
                  onMove={(delta) => move(i, delta)}
                  drag={drag}
                  onToggleVisible={() => setField(r.stage_key, { is_visible: !r.is_visible })}
                />
              </div>
            ))}
            {hasNoStageTemplates && (
              <>
                <div className="px-[14px] pb-1 pt-4 text-[12px] font-medium text-[#98A2B3]">Other</div>
                <button
                  type="button"
                  onClick={() => setSelectedKey(NO_STAGE)}
                  className={`flex h-10 w-full cursor-pointer items-center gap-3 rounded-lg border-0 pl-[17px] pr-2 text-left transition-colors duration-150 ${onNoStage ? "bg-[#EFF5FC]" : "bg-transparent hover:bg-[#F9FAFB]"}`}
                >
                  <MessageSquare size={15} className="text-[#667085]" aria-hidden="true" />
                  <span className={`text-[13.5px] ${onNoStage ? "font-semibold text-[#1F5596]" : "font-medium text-[#344054]"}`}>Other messages</span>
                </button>
              </>
            )}
          </div>
        </nav>
        <JourneyPreview
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          rows={rows}
          engine={engine}
          picks={picks}
          usable={usable}
          templates={templates}
          onOpenStage={(key) => {
            setPreviewOpen(false);
            setSelectedKey(key);
          }}
        />

        <div ref={detailRef} className={`scroll-mt-[88px] ${cardClass} px-8 pb-8 pt-7`}>
          {onNoStage ? (
            <>
              <h2 className="mb-7 text-[22px] font-semibold tracking-[-0.01em] text-[#101828]">Other messages</h2>
              <TemplateList
                templates={templates}
                filterKey={NO_STAGE}
                lock={{ locked: false, reason: "" }}
                errors={errors.templates}
                onPatch={patchTemplate}
                usable={usable}
                fields={fields}
                picks={picks}
                onPick={setPick}
                stepKeys={stepKeys}
              />
            </>
          ) : selectedRow ? (
            <>
              <StageHeader
                row={selectedRow}
                index={selectedIndex}
                total={rows.length}
                error={errors.label[selectedRow.stage_key]}
                onRename={(label) => setField(selectedRow.stage_key, { label })}
                onVisible={(on) => setField(selectedRow.stage_key, { is_visible: on })}
                onDelete={selectedRow.is_custom ? () => removeStage(selectedRow.stage_key) : null}
              />
              {selectedRow.is_custom ? (
                <StageForm
                  key={selectedRow.stage_key}
                  row={selectedRow}
                  templates={usable}
                  fields={fields}
                  pick={picks[selectedRow.stage_key] || null}
                  onPick={(p) => setPick(selectedRow.stage_key, p)}
                  onChange={(patch) => setField(selectedRow.stage_key, patch)}
                />
              ) : (
                builtIn()
              )}
            </>
          ) : null}
          {saveBar}
        </div>
      </div>
      {addingTemplate && (
        <AddExistingTemplate tenantId={tenantId} onClose={() => setAddingTemplate(null)} onAdded={addingTemplate.onAdded} />
      )}
    </AddTemplateContext.Provider>
  );
}
